import React, { useState } from "react";
import { BookOpenText, ChevronDown, Check, Loader2 } from "lucide-react";
import { useReadingLevel } from "../context/ReadingLevelContext";
import { READING_LEVELS } from "../lib/copyAdaptApi";

/**
 * ReadingLevelPill — compact dropdown that lives in the app header.
 *
 * Visual language:
 *   • Idle state = small icon-only button
 *   • Open state = menu with 3 tiers, current level checked
 *
 * The pill is intentionally SMALL — this is a reader preference, not a
 * primary action. It sits alongside the search bar and profile menu.
 */
export default function ReadingLevelPill() {
  const { level, setLevel } = useReadingLevel();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const current = READING_LEVELS.find((l) => l.key === level) || READING_LEVELS[2];

  // Visibility is now driven by Admin Console → Menu Directory
  // (item id: `header-reading-level`). The AppShell header gates the
  // render around `disabledSet`, so hiding the pill again is a one-click
  // admin toggle — no code change required.

  const pick = async (next) => {
    if (next === level) { setOpen(false); return; }
    setBusy(true);
    try { await setLevel(next); }
    finally { setBusy(false); setOpen(false); }
  };

  return (
    <div className="relative" data-testid="reading-level-pill">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-shell-border/70 text-xs font-medium text-foreground/85 hover:border-shell-accent hover:text-foreground transition"
        title={`Reading level: ${current.label} — click to change`}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="reading-level-trigger"
      >
        <BookOpenText className="w-3.5 h-3.5 text-shell-accent" />
        <span className="hidden sm:inline">{current.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          {/* Click-away catcher */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            role="listbox"
            className="absolute right-0 mt-2 w-72 z-50 rounded-xl border border-shell-border bg-white shadow-2xl overflow-hidden"
            data-testid="reading-level-menu"
          >
            <div className="px-4 py-3 border-b border-shell-border/60 bg-muted/40">
              <div className="text-[10px] uppercase tracking-widest text-shell-accent font-semibold">
                Reading level
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground leading-snug">
                Choose how technical you want the copy across the platform.
              </div>
            </div>
            <ul className="py-1">
              {READING_LEVELS.map((opt) => {
                const isCurrent = opt.key === level;
                return (
                  <li key={opt.key}>
                    <button
                      type="button"
                      onClick={() => pick(opt.key)}
                      disabled={busy}
                      className={`w-full flex items-start gap-3 px-4 py-2.5 text-left transition ${
                        isCurrent ? "bg-shell-accent/[0.07]" : "hover:bg-muted/50"
                      }`}
                      data-testid={`reading-level-option-${opt.key}`}
                    >
                      <div className="w-5 mt-0.5 shrink-0 text-shell-accent">
                        {busy && isCurrent ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : isCurrent ? (
                          <Check className="w-4 h-4" />
                        ) : null}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground">
                          {opt.label}
                        </div>
                        <div className="text-xs text-muted-foreground leading-snug mt-0.5">
                          {opt.hint}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="px-4 py-2 border-t border-shell-border/60 text-[10px] text-muted-foreground bg-muted/30">
              Powered by Claude Sonnet 4.6 · Instant after first read
            </div>
          </div>
        </>
      )}
    </div>
  );
}
